import { BiddingApprovalMode } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Alert,
  Box,
  Link,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { WarningMessagesAlert } from '~/components/bidding/common/WarningMessagesAlert'
import type { ApprovalModeGroup } from '~/components/bidding/common/approvalModeGroups'
import { AlertActions } from '~/components/common/AlertActions'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { convertAreaLabel } from '~/models/area'
import { routePaths } from '~/routes/routePaths'

/**
 * 入札締切までの残り時間の表示形式 (= 表示専用の完成済み部品、 呼び出し元が
 * `formatGcRemainingTime` 等で t() 済み)。
 *
 * 残り 24 時間以内は `countdown` (秒単位カウントダウン)、 超えている間は `datetime`
 * (締切日時そのもの) を渡す (issue #8320、 Des 確定 2026-09-18。 秒単位のカウントダウンは
 * 24 時間を超えると常時再描画するだけで業務上の意味を持たないため)。
 */
export type DeadlineDisplay =
  | { kind: 'countdown'; time: string }
  | { kind: 'datetime'; date: string; time: string }

/** 違反種別 1 つぶんのインライン表示内容 (= Alert 1 個)。 */
export type ViolationSection = {
  /** 見出し (件数込み。例: 「入札制限違反 3 件あり」)。 */
  title: string
  /** 解消手順を伝える本文。 仕様に文言が無い違反・警告は省略する。 */
  body?: string
  /** 違反行の明細。先頭 MAX_VIOLATION_DETAILS 件まで表示する。 */
  details: readonly string[]
}

/**
 * 提出対象と既存札の重複確認の表示状態。判定そのものは backend が担い、この component
 * は確認が未完了であることと返された件数だけを表示する。
 */
export type SubmissionDuplicateCheck =
  | { kind: 'checking' }
  | { kind: 'failed'; onRetry: () => void }
  | { kind: 'duplicates'; count: number; bidKind: 'spot' | 'block' }

/**
 * JEPX への送信のタイミング (確認項目「JEPX への送信」、 #9586)。
 *
 * - `onSubmit`: 提出するとすぐに送信 (承認なし・自動)
 * - `afterApproval`: 承認申請。 承認されると同じ操作で提出まで進む (スポット・ブロック
 *   AC-BSP-094、 時間前 AC-BIN-095) ので「承認されると自動で送信」
 * - `mixed`: 承認申請する入札と、 すぐに送信する入札が混ざる (札ごとにそのエリアの
 *   承認モードで承認申請か提出かが決まる、 AC-BSP-076 / AC-BIN-009)
 *
 * 文言は市場で異なる (受付開始前は予約入札の文言、 時間前入札は開場時刻)。 スポット・ブロックは
 * spec の AC-BSP-076 のとおり書く。
 */
export type SubmitSendTiming = 'onSubmit' | 'afterApproval' | 'mixed'

/**
 * 提出確認モーダルの確認項目 (#9586、 2026-10-05 デザイナー判断)。 以前は説明文に埋めていた
 * 承認フロー・ JEPX への送信のタイミング・提出後の取下 (取消) の期限を、 件数・合計と同じ枠に
 * 1 行ずつ出す。 design `bidding/spot.html` の `bsc-approval-mode` / `bsc-send` と
 * `bidding/intraday.html` の確認項目 (`js/approval-modes.js`)。
 */
export type SubmitConfirmItems = {
  /** 市場。 承認フロー設定へのリンク先と、 取下 (スポット) / 取消 (時間前) の行を決める。 */
  market: 'spot' | 'intraday'
  /**
   * 提出対象のエリアの承認モード ([[groupAreasByApprovalMode]])。 省略 / 空なら「承認フロー」
   * の行を出さない (承認済の札を提出する確認のように、 承認モードがこの先の流れを左右しない時。
   * 対象のエリアを確定できない時も出さない)。
   */
  approvalModes?: readonly ApprovalModeGroup[]
  send: SubmitSendTiming
  /**
   * 予約になる時の JEPX への送信時刻 (表示用の完成済み文字列)。 スポットは入札受付開始前
   * (予約入札、 AC-BSP-092) の受付開始日時 (M/D HH:MM)、 時間前は市場の未開場中の
   * 開場時刻 (AC-BIN-089)。 渡すと「JEPX への送信」 の値と撤回の行を予約向けに書き分ける
   * (AC-BSP-076 / AC-BIN-060)。 省略時は予約にならない (受付中・開場中)。
   */
  reservedOpenAt?: string
}

type Props = {
  isOpen: boolean
  /** モーダルタイトル (= 省略時は「JEPX に提出」)。 承認申請確認でも流用するため省略可。 */
  title?: string
  /** 確定ボタンのラベル (= 省略時は「提出する」)。 */
  confirmLabel?: string
  /**
   * 送信中の確定ボタンのラベル (= 省略時は「提出中…」)。 提出以外に流用する
   * 呼び出し元 (承認確認 modal 等) が操作名に合わせて差し替える。
   */
  submittingLabel?: string
  /**
   * 本文 (= 省略時は提出用文言)。 承認申請確認では取消可能な旨の文言を渡す。
   * null なら本文の段落を出さない (承認確認のように、 仕様に本文の文言が無い時)。
   */
  body?: string | null
  /** 札件数 (= summary 表示用)。 */
  bidCount: number
  /**
   * 絞り込みで一覧に表示していないため、 この確認の対象に入らない札の件数。 1 以上の時だけ、
   * 件数の下に「対象外です」 の 1 行を出す (省略 / 0 は出さない)。
   */
  excludedByFilterCount?: number
  /**
   * 件数行の文言 (= 既定は「札件数: {{count}} 件」)。 札とは別の単位で数える
   * 呼び出し元が単位を明示するために差し替える (例: ブロック入札の取消は
   * BlockBidGroup 単位なので「対象: グループ N 件 (M 札)」)。 完成済みの文字列を
   * 渡す (= 呼び出し元が t() 済み)。
   */
  countLabel?: string
  /**
   * 買 数量合計。undefined のときは買 行を描画しない
   * (= ブロック入札など数量集計が本 PR スコープ外の呼び出し元から省略可)。
   * 単位は `quantityUnit` に従う (= 呼び出し元が決める。 spot 通常入札は AC-BSP-053 に
   * 従い MW の単純合算を渡す、 issue #6266)。
   */
  totalBuyQuantity?: number
  /** 売 数量合計。undefined のときは売 行を描画しない。 */
  totalSellQuantity?: number
  /**
   * エリア別内訳 (= 合計行の下に 1 エリア 1 行で並べる)。 省略 / 空配列なら描画しない。
   *
   * spec は承認確認モーダル (AC-BSP-097) と承認申請確認モーダル (AC-BSP-007) の
   * 申請内容サマリに「件数、 買/売数量(MW)、 エリア別内訳」 を要求している。 数量の単位は
   * 合計行と同じ `quantityUnit` に従う (= 呼び出し元が決める)。
   */
  areaBreakdown?: ReadonlyArray<{
    /** エリア表示名 (= 呼び出し元が `convertAreaLabel` 済み)。 */
    areaLabel: string
    count: number
    buyQuantity: number
    sellQuantity: number
    /**
     * 行に併記する受渡契約コードの表示文字列 (= 呼び出し元が t() 済み。 複数コードの区切りや
     * 「コードを持たない札」 の扱いも呼び出し元が決める)。 省略した行は件数と数量だけ出す。
     */
    deliveryContractCodeText?: string
  }>
  /**
   * 札単位の明細行 (= 件数・合計では表現できない項目を出す枠)。 省略 / 空配列なら
   * 描画しない。
   *
   * 1 モーダル 1 札で承認する呼び出し元 (時間前入札の承認確認、 AC-BIN-097) は
   * 申請内容サマリに「対象コマ・価格」 まで求められており、 集計 (件数 / 合計 /
   * エリア別内訳) では表せない。 完成済みの文字列を渡す (= 呼び出し元が t() 済み)。
   */
  detailLines?: readonly string[]
  /**
   * 確認項目 (承認フロー・ JEPX への送信・取下 / 取消、 [[SubmitConfirmItems]])。 提出・
   * 承認申請の確認だけが渡す (承認 / 申請取消の確認では出さない)。 省略時は出さない。
   */
  confirmItems?: SubmitConfirmItems
  /** 数量合計の単位ラベル (= 既定 'MW')。 */
  quantityUnit?: string
  hasViolation: boolean
  /** 確定ボタンが押せない理由 (非活性の時だけ tooltip に出す)。省略時は出さない。 */
  confirmDisabledReason?: string
  /**
   * 違反種別ごとのインライン明細 (= AC-BSP-008 の入札制限違反 / AC-BSP-021 の
   * JEPX 階段数超過)。渡された種別ぶんだけ Alert を積む。省略時は汎用文言の
   * Alert 1 個に退避する (AC-BSP-052: 違反種別は区別して表示する)。
   */
  violationSections?: readonly ViolationSection[]
  /** 提出前サマリーより上に置く、対象外などの operation-scoped な違反表示。 */
  leadingViolationSections?: readonly ViolationSection[]
  /**
   * 確定を止めない注意 (= warning Alert)。 違反明細と同じ形で積むが `hasViolation` には
   * 寄与しない。 例: 承認確認 modal の売買逆転チェック結果 (AC-BSP-097 が列挙する
   * 「入札制限チェック結果, 売買逆転チェック結果」 のうち、 売買逆転は spec §2 が
   * 「警告表示するが意図的な場合もあるためブロックはしない」 と定める)。
   * `details` が空の section は描画しない。
   */
  warningSections?: readonly ViolationSection[]
  /**
   * 確定を止めない、 見出し・明細を持たない注意の文 (例: AC-BSP-098 の承認者でない・自己承認の
   * 再確認)。 空・省略時は描画しない。
   */
  warningMessages?: readonly string[]
  /**
   * 提出対象と既存札の重複確認結果 (AC-BSP-114)。未確認は既存の提出可否を変えない。
   */
  duplicateCheck?: SubmissionDuplicateCheck
  /** 市場名ラベル (= 「スポット」 / 「時間前」)。 デフォルト本文の "JEPX {marketLabel}市場" に入る。 */
  marketLabel?: string
  /**
   * 入札締切までの残り時間表示 (= 表示専用の完成済み部品、 呼び出し元が
   * `formatGcRemainingTime` 等で t() 済み)。 省略時は表示しない。
   *
   * 承認申請確認 modal が「モーダル表示中は入札締切までの残り時間を表示する」
   * (AC-BSP-012) ことを求めるための専用枠。 提出確認 / 承認確認 / 申請取消確認の
   * 各 modal には同 AC の適用が無いため、 呼び出し元は spot の承認申請確認だけが渡す。
   * 24 時間を超える残り時間はカウントダウンでなく締切日時を出す
   * ([[DeadlineDisplay]]、 issue #8320)。
   */
  deadlineDisplay?: DeadlineDisplay
  /**
   * 本文の下に追加で出す注記 (省略時は非表示)。 info Alert 1 個で描画する。
   * 例: 時間前の承認確認で入札制限を再検証できない時の案内。 予約になる時の送信時刻は
   * ここでなく確認項目 (`confirmItems.reservedOpenAt`) で示す (AC-BIN-060)。
   */
  note?: React.ReactNode
  /** 注記 (`note`) の見出し。 省略時は見出しなしで本文だけ出す。 */
  noteTitle?: string
  /** 提出後の結果表示。指定時は提出前サマリーと確定操作を隠し、閉じるだけを表示する。 */
  result?: { title: string }
  isSubmitting?: boolean
  onConfirm: () => Promise<void> | void
  onClose: () => void
}

/**
 * 違反明細をモーダル内に出す上限行数 (超過分は「…他 N 件」に丸める)。
 * design `spot.html` の `bsc-violation-list` / `bsc-stairs-list` と同じ 20 件。
 */
const MAX_VIOLATION_DETAILS = 20

/** 違反種別 1 つぶんの Alert (= 見出し + 解消手順 + 明細行)。 warning は確定を止めない注意。 */
export const ViolationAlert = ({
  section,
  status,
}: {
  section: ViolationSection
  status: 'error' | 'warning'
}): React.ReactElement => {
  const { t } = useTranslation()
  const shown = section.details.slice(0, MAX_VIOLATION_DETAILS)
  const hiddenCount = section.details.length - shown.length
  return (
    <Alert
      description={
        <VStack align="stretch" gap={1}>
          {section.body !== undefined && <Text>{section.body}</Text>}
          {shown.map((detail) => (
            <Text key={detail}>・{detail}</Text>
          ))}
          {hiddenCount > 0 && (
            <Text>
              {t('page:biddingCommon.submitConfirm.violationMore', {
                count: hiddenCount,
              })}
            </Text>
          )}
        </VStack>
      }
      status={status}
      title={section.title}
    />
  )
}

/**
 * 確認項目の行 (承認フロー・ JEPX への送信・取下 / 取消、 #9586)。 件数・合計と同じ
 * 「項目: 値」 の行で並べる。
 *
 * 撤回の行は確認の種類と予約かどうかで書き分ける (AC-BSP-076 / AC-BIN-060):
 * 承認申請 (`send` = `afterApproval`) は「申請取消」、 予約になる提出は「予約取消・取下」
 * (時間前は「予約取消・取消」)、 それ以外は「取下」 (時間前は「取消」)。
 */
const ConfirmItemRows = ({
  items,
}: {
  items: SubmitConfirmItems
}): React.ReactElement => {
  const { t } = useTranslation()

  // 承認モードの呼び名は承認フロー設定画面と同じ語を使う (同じ設定値を指すため)。
  const modeLabel = (mode: BiddingApprovalMode): string => {
    switch (mode) {
      case BiddingApprovalMode.NO_APPROVAL:
        return t('page:adminApprovalFlow.mode.noApproval')
      case BiddingApprovalMode.AUTO:
        return t('page:adminApprovalFlow.mode.auto')
      default:
        return t('page:adminApprovalFlow.mode.approval')
    }
  }
  const groups = items.approvalModes ?? []
  const [onlyGroup] = groups
  // 1 種類ならモード名だけ、 エリアで混ざる時は「要承認 (中部・関西)、 承認なし (東京)」。
  const approvalModeText =
    groups.length === 1 && onlyGroup !== undefined
      ? modeLabel(onlyGroup.mode)
      : groups
          .map((group) =>
            t('page:biddingCommon.submitConfirm.items.approvalModeGroup', {
              mode: modeLabel(group.mode),
              areas: group.areaCodes
                .map((area) => convertAreaLabel(area))
                .join(
                  t('page:biddingCommon.submitConfirm.items.areaSeparator'),
                ),
            }),
          )
          .join(t('page:biddingCommon.submitConfirm.items.groupSeparator'))

  const isSpot = items.market === 'spot'
  const openAt = items.reservedOpenAt

  const sendText = (
    openAt === undefined
      ? isSpot
        ? {
            onSubmit: t('page:biddingCommon.submitConfirm.items.sendOnSubmit'),
            afterApproval: t(
              'page:biddingCommon.submitConfirm.items.sendAfterApproval',
            ),
            mixed: t('page:biddingCommon.submitConfirm.items.sendMixed'),
          }
        : {
            onSubmit: t('page:biddingCommon.submitConfirm.items.sendOnSubmit'),
            afterApproval: t(
              'page:biddingCommon.submitConfirm.items.intraday.sendAfterApproval',
            ),
            mixed: t(
              'page:biddingCommon.submitConfirm.items.intraday.sendMixed',
            ),
          }
      : isSpot
        ? {
            onSubmit: t(
              'page:biddingCommon.submitConfirm.items.spotReserved.sendOnSubmit',
              { openAt },
            ),
            afterApproval: t(
              'page:biddingCommon.submitConfirm.items.spotReserved.sendAfterApproval',
              { openAt },
            ),
            mixed: t(
              'page:biddingCommon.submitConfirm.items.spotReserved.sendMixed',
              { openAt },
            ),
          }
        : {
            onSubmit: t(
              'page:biddingCommon.submitConfirm.items.intradayReserved.sendOnSubmit',
              { openAt },
            ),
            afterApproval: t(
              'page:biddingCommon.submitConfirm.items.intradayReserved.sendAfterApproval',
              { openAt },
            ),
            mixed: t(
              'page:biddingCommon.submitConfirm.items.intradayReserved.sendMixed',
              { openAt },
            ),
          }
  )[items.send]

  const withdrawRow =
    items.send === 'afterApproval'
      ? {
          label: t('page:biddingCommon.submitConfirm.items.requestCancel'),
          value: isSpot
            ? t('page:biddingCommon.submitConfirm.items.requestCancelValueSpot')
            : t(
                'page:biddingCommon.submitConfirm.items.requestCancelValueIntraday',
              ),
        }
      : openAt !== undefined
        ? isSpot
          ? {
              label: t(
                'page:biddingCommon.submitConfirm.items.spotReserved.withdraw',
              ),
              value: t(
                'page:biddingCommon.submitConfirm.items.spotReserved.withdrawValue',
              ),
            }
          : {
              label: t(
                'page:biddingCommon.submitConfirm.items.intradayReserved.cancel',
              ),
              value: t(
                'page:biddingCommon.submitConfirm.items.intradayReserved.cancelValue',
              ),
            }
        : isSpot
          ? {
              label: t('page:biddingCommon.submitConfirm.items.withdraw'),
              value: t('page:biddingCommon.submitConfirm.items.withdrawValue'),
            }
          : {
              label: t('page:biddingCommon.submitConfirm.items.cancel'),
              value: t('page:biddingCommon.submitConfirm.items.cancelValue'),
            }

  return (
    <>
      {groups.length > 0 && (
        <Text>
          {t('page:biddingCommon.submitConfirm.items.row', {
            label: t('page:biddingCommon.submitConfirm.items.approvalFlow'),
            value: approvalModeText,
          })}
          {/* 承認フロー設定の該当プロセスを開く (design の値の横の「承認フロー設定」)。
              同じタブで開く (2026-10-16 デザイナー決定「別途指定の無いリンクは別タブで
              開かない」。 spec AC-BSP / AC-BIN の確認項目は「横に承認フロー設定への
              リンクを置く」 だけで開き方を指定していない)。 確認中の札は DRAFT として
              保存済みなので、 移動しても失われない。 */}
          <Link marginLeft={2} asChild>
            <RouterLink
              to={`${routePaths.adminApprovalFlow.path}?process=${isSpot ? 'spot-bid' : 'intraday-bid'}`}
            >
              {t('page:biddingCommon.submitConfirm.items.approvalFlowLink')}
            </RouterLink>
          </Link>
        </Text>
      )}
      <Text>
        {t('page:biddingCommon.submitConfirm.items.row', {
          label: t('page:biddingCommon.submitConfirm.items.send'),
          value: sendText,
        })}
      </Text>
      {/* 提出後の取下 (スポット、 AC-BSP-076) / 取消 (時間前、 AC-BIN-060) の期限。
          承認申請では申請取消の期限 (上の [[ConfirmItemRows]] の JSDoc 参照)。 */}
      <Text>
        {t('page:biddingCommon.submitConfirm.items.row', withdrawRow)}
      </Text>
    </>
  )
}

/**
 * JEPX 提出確認 modal / 承認申請確認 modal (= spot / intraday 共通)。
 * 設計の鉄則 1 (= 二重確認) を再現。
 * `title` / `confirmLabel` / `body` を省略した場合は市場名を埋め込んだ提出用文言で動作する。
 * 制限違反がある状態では確定ボタンを disabled に。
 *
 * 実際の提出 RPC は呼び出し側 (= 各ページ) の `onConfirm` で行う
 * (spot: 下書きからの承認申請・提出は TransitionBiddingSpotDrafts / intraday: 注文ごとの
 * TransitionBiddingIntradayOrderState(SUBMIT))。
 */
export const SubmitConfirmModal = ({
  isOpen,
  title,
  confirmLabel,
  submittingLabel,
  body,
  bidCount,
  excludedByFilterCount,
  countLabel,
  totalBuyQuantity,
  totalSellQuantity,
  areaBreakdown,
  detailLines,
  confirmItems,
  quantityUnit = 'MW',
  hasViolation,
  confirmDisabledReason,
  violationSections,
  leadingViolationSections,
  warningSections,
  warningMessages,
  duplicateCheck,
  marketLabel,
  deadlineDisplay,
  note,
  noteTitle,
  result,
  isSubmitting = false,
  onConfirm,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const market = marketLabel ?? t('page:biddingSpot.market.label')
  const resolvedTitle = title ?? t('page:biddingCommon.submitConfirm.title')
  const resolvedConfirmLabel =
    confirmLabel ?? t('page:biddingCommon.submitConfirm.confirm')
  const resolvedBody =
    body === undefined
      ? t('page:biddingCommon.submitConfirm.description', { market })
      : body

  const sections = (violationSections ?? []).filter((s) => s.details.length > 0)
  const leadingSections = (leadingViolationSections ?? []).filter(
    (s) => s.details.length > 0,
  )
  const warnings = (warningSections ?? []).filter((s) => s.details.length > 0)

  const footer =
    result === undefined ? (
      <SaveCancelFooter
        isPending={isSubmitting}
        isSaveDisabled={hasViolation}
        onCancel={onClose}
        onSave={() => {
          void onConfirm()
        }}
        saveDisabledReason={hasViolation ? confirmDisabledReason : undefined}
        saveLabel={resolvedConfirmLabel}
        saveLoadingText={
          submittingLabel ?? t('page:biddingCommon.submitConfirm.submitting')
        }
      />
    ) : (
      <TertiaryButton onClick={onClose}>
        {t('common:action.close')}
      </TertiaryButton>
    )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={result?.title ?? resolvedTitle}
    >
      <VStack align="stretch" gap={3}>
        {result === undefined && resolvedBody !== null && (
          <Text>{resolvedBody}</Text>
        )}
        {/* 入札締切までの残り時間 (AC-BSP-012)。 承認申請確認 modal だけが渡す。
            24 時間を超える間はカウントダウンでなく締切日時を出す (issue #8320)。 */}
        {result === undefined && deadlineDisplay !== undefined && (
          <Text fontWeight="bold">
            {deadlineDisplay.kind === 'countdown'
              ? t('page:biddingCommon.submitConfirm.deadlineRemaining', {
                  time: deadlineDisplay.time,
                })
              : t('page:biddingCommon.submitConfirm.deadlineAt', {
                  date: deadlineDisplay.date,
                  time: deadlineDisplay.time,
                })}
          </Text>
        )}
        {result === undefined && note !== undefined && (
          <Alert description={note} status="info" title={noteTitle} />
        )}
        {result === undefined && (
          <WarningMessagesAlert messages={warningMessages ?? []} />
        )}
        {leadingSections.map((section) => (
          <ViolationAlert
            key={section.title}
            section={section}
            status="error"
          />
        ))}
        {result === undefined && (
          <Box
            padding={3}
            fontSize="sm"
            borderRadius="md"
            backgroundColor="Surface.secondary"
          >
            <Text>
              {countLabel ??
                t('page:biddingCommon.submitConfirm.bidCount', {
                  count: bidCount,
                })}
            </Text>
            {excludedByFilterCount !== undefined &&
              excludedByFilterCount > 0 && (
                <Text>
                  {t('page:biddingCommon.submitConfirm.excludedByFilter', {
                    count: excludedByFilterCount,
                  })}
                </Text>
              )}
            {detailLines !== undefined &&
              detailLines.map((line, index) => (
                <Text key={`${index}:${line}`}>{line}</Text>
              ))}
            {totalBuyQuantity !== undefined && (
              <Text>
                {t('page:biddingCommon.submitConfirm.totalBuy', {
                  quantity: totalBuyQuantity.toFixed(1),
                  unit: quantityUnit,
                })}
              </Text>
            )}
            {totalSellQuantity !== undefined && (
              <Text>
                {t('page:biddingCommon.submitConfirm.totalSell', {
                  quantity: totalSellQuantity.toFixed(1),
                  unit: quantityUnit,
                })}
              </Text>
            )}
            {confirmItems !== undefined && (
              <ConfirmItemRows items={confirmItems} />
            )}
            {/* エリア別内訳 (AC-BSP-097 / AC-BSP-007)。 合計行と同じ枠内に続けて出す
              (= 「この申請の中身」 を 1 ブロックで読ませる)。 */}
            {areaBreakdown !== undefined && areaBreakdown.length > 0 && (
              <Box marginTop={2}>
                <Text textStyle="captionStrong">
                  {t('page:biddingCommon.submitConfirm.areaBreakdownTitle')}
                </Text>
                {areaBreakdown.map((row) => (
                  <Text key={row.areaLabel}>
                    {t(
                      row.deliveryContractCodeText === undefined
                        ? 'page:biddingCommon.submitConfirm.areaBreakdownRow'
                        : 'page:biddingCommon.submitConfirm.areaBreakdownRowWithCode',
                      {
                        area: row.areaLabel,
                        count: row.count,
                        buy: row.buyQuantity.toFixed(1),
                        sell: row.sellQuantity.toFixed(1),
                        unit: quantityUnit,
                        codes: row.deliveryContractCodeText,
                      },
                    )}
                  </Text>
                ))}
              </Box>
            )}
          </Box>
        )}
        {sections.map((section) => (
          <ViolationAlert
            key={section.title}
            section={section}
            status="error"
          />
        ))}
        {hasViolation &&
          sections.length === 0 &&
          leadingSections.length === 0 && (
            <Alert
              description={t('page:biddingCommon.submitConfirm.violationBody')}
              status="error"
              title={t('page:biddingCommon.submitConfirm.violationTitle')}
            />
          )}
        {result === undefined &&
          warnings.map((section) => (
            <ViolationAlert
              key={section.title}
              section={section}
              status="warning"
            />
          ))}
        {result === undefined && duplicateCheck?.kind === 'checking' && (
          <Alert
            status="info"
            title={t(
              'page:biddingCommon.submitConfirm.duplicateCheck.checking',
            )}
          />
        )}
        {result === undefined && duplicateCheck?.kind === 'failed' && (
          <Alert
            description={t(
              'page:biddingCommon.submitConfirm.duplicateCheck.failedBody',
            )}
            status="warning"
            title={t(
              'page:biddingCommon.submitConfirm.duplicateCheck.failedTitle',
            )}
          >
            <AlertActions>
              <TertiaryButton onClick={duplicateCheck.onRetry} size="sm">
                {t('page:biddingCommon.submitConfirm.duplicateCheck.retry')}
              </TertiaryButton>
            </AlertActions>
          </Alert>
        )}
        {result === undefined &&
          duplicateCheck?.kind === 'duplicates' &&
          duplicateCheck.count > 0 && (
            <Alert
              description={t(
                duplicateCheck.bidKind === 'spot'
                  ? 'page:biddingCommon.submitConfirm.duplicateCheck.spotBody'
                  : 'page:biddingCommon.submitConfirm.duplicateCheck.blockBody',
              )}
              status="warning"
              title={t(
                'page:biddingCommon.submitConfirm.duplicateCheck.duplicateTitle',
                { count: duplicateCheck.count },
              )}
            />
          )}
      </VStack>
    </Modal>
  )
}
